import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils, { getWorkMinutesPerDay, MINUTES_PER_DAY } from '../../../util/dates';
import { getDetailsData } from '../FlowEfficiencyActions';
import dataUtils from '../../../util/dataUtils';
import { isWhite } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';

import highchartFactory from '../../../util/highcharts';
import chartUtils from '../../../util/chartUtils';
const highchartUtils = highchartFactory(Highcharts);

// both views and the navigator under them start where the dot view's y title and its "100 days" labels end
const PLOT_LEFT = 96;
// the TV draws the app's sizes at 4/3 (its 16px labels against the app's 12px)
const TV_PLOT_LEFT = 128;

const chartConfigColumn = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    resetZoomButton: chartUtils.RESET_ZOOM_BESIDE_BUTTONS,
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: PLOT_LEFT,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    lineColor: chartUtils.TREND_AXIS_LINE_COLOR,
    tickColor: chartUtils.TREND_AXIS_LINE_COLOR,
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px'
      }
    },
    min: 0,
    title: {
      text: 'Flow efficiency (%)',
      style: {
        fontSize: '13px'
      }
    },
    plotLines: [],
    events: {}
  },
  yAxis: {
    allowDecimals: false,
    labels: {
      formatter: function() {
        return chartUtils.withUnit(this.value, 'item');
      },
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px',
        whiteSpace: 'nowrap'
      }
    },
    min: 0,
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    gridLineWidth: 1,
    title: {
      text: null
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      color: chartUtils.TREND_LINE_COLOR,
      point: {
        events: {}
      }
    },
    column: {
      pointPadding: 0.06,
      borderWidth: 0,
      groupPadding: 0,
      shadow: false,
      inactiveOtherPoints: true,
      states: chartUtils.BAR_STATES
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    shared: true,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};

const chartConfigScatterplot = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'scatter',
    zoomType: 'xy',
    resetZoomButton: chartUtils.RESET_ZOOM_BESIDE_BUTTONS,
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: PLOT_LEFT,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    lineColor: chartUtils.TREND_AXIS_LINE_COLOR,
    tickColor: chartUtils.TREND_AXIS_LINE_COLOR,
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px'
      }
    },
    min: 0,
    title: {
      text: 'Flow efficiency (%)',
      style: {
        fontSize: '13px'
      }
    },
    plotLines: [],
    events: {}
  },
  yAxis: {
    allowDecimals: false,
    labels: {
      formatter: function() {
        return chartUtils.withUnit(this.value, 'day');
      },
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px'
      }
    },
    min: 0,
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    gridLineWidth: 1,
    title: {
      text: 'Cycle time (days)',
      style: {
        fontSize: '13px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 7,
        lineWidth: 1,
        lineColor: '#FFFFFF',
        states: {
          hover: chartUtils.hoverMarker(),
          select: chartUtils.selectedMarker(10)
        }
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    },
    series: {
      point: {
        events: {}
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    shared: true,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0
  },
  series: [
    {
      name: 'Data'
    }
  ]
};

const masterchartConfig = {
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  chart: {
    reflow: false,
    borderWidth: 0,
    backgroundColor: null,
    marginLeft: PLOT_LEFT,
    marginRight: 10,
    zoomType: 'xy',
    type: 'scatter',
    events: {}
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    showLastTickLabel: true,
    plotBands: [
      {
        id: 'mask-before',
        color: chartUtils.ZOOM_MASK_COLOR
      }
    ],
    title: {
      text: null
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    }
  },
  yAxis: {
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Cycle time (days)',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      formatter: function() {
        return Math.round(this.value / MINUTES_PER_DAY);
      },
      style: {
        fontSize: '12px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 5
      }
    }
  },
  tooltip: {
    formatter: function() {
      return false;
    }
  },
  legend: {
    enabled: false
  },
  series: [
    {
      name: 'Data'
    }
  ]
};

export class FlowEfficiencyChart extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();
    this.masterContainer = React.createRef();
    this.state = {};

    this.onSelection = this.onSelection.bind(this);
    this.updateDetails = this.updateDetails.bind(this);
  }

  plotLeft = () => (this.props.wallmode ? TV_PLOT_LEFT : PLOT_LEFT);

  chartConfig = () => {
    const { chartType } = this.props;
    const config = chartType === 'scatterplot' ? _.cloneDeep(chartConfigScatterplot) : _.cloneDeep(chartConfigColumn);
    config.chart.marginLeft = this.plotLeft();
    return config;
  };

  componentDidMount = () => {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    const chartConfig = this.chartConfig();
    chartConfig.exporting = this.props.chartExporting;
    masterchartConfig.chart.events.selection = this.onSelection;

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(this);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };
    chartConfig.chart.events.render = function() {
      that.selectionHalo = chartUtils.drawSelectionHalo(that.chart, that.selectionHalo);
      chartUtils.roundTops(this);
    };

    this.master = new Highcharts['Chart'](this.masterContainer.current, { ...masterchartConfig, chart: { ...masterchartConfig.chart, marginLeft: this.plotLeft() } });
    this.chart = new Highcharts['Chart'](this.chartContainer.current, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);
    this.updateYAxisFormatter();

    if (this.props.data.series) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isPointSelected && !this.props.isPointSelected && this.chart) {
      this.chart.getSelectedPoints().forEach(point => point.select(false, true));
      this.selectionHalo = chartUtils.drawSelectionHalo(this.chart, this.selectionHalo);
    }
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.updateChart(prevProps.data);
      if (!this.props.details.length && this.props.queueLists) {
        this.props.getDetailsData(this.props.dates);
      }
    }

    if (this.props.chartType !== prevProps.chartType) {
      if (this.chart) {
        for (let i = this.chart.series.length - 1; i >= 0; i--) {
          this.chart.series[i].remove(false);
        }
        const chartConfig = this.chartConfig();
        this.chart.update(chartConfig);
      }
    }

    if (this.props.data.series && prevProps.queueLists && !_.isEqual(prevProps.queueLists, this.props.queueLists)) {
      this.props.getDetailsData(this.props.dates);
    }

    if (
      this.props.data.series &&
      (prevProps.details != this.props.details || prevProps.effRange != this.props.effRange)
    ) {
      this.updateDetails();
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (!_.isEqual(prevProps.excludeWeekendOpts, this.props.excludeWeekendOpts)) {
      this.updateYAxisFormatter();
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.master.reflow();
      this.chart.reflow();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Flow Efficiency Chart',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },          
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
    this.master.destroy();
  }

  updateChart(oldData = {}) {
    this.master.update(
      {
        xAxis: {
          plotBands: [
            {
              id: 'mask-before',
              color: 'rgba(0, 0, 0, 0.2)'
            }
          ]
        }
      },
      false
    );

    const diff = dataUtils.getDiff(oldData.series, this.props.data.series, 'data');
    highchartUtils.updateSeries(diff, this.master);
    this.master.redraw();

    this.updateDetails();
  }

  updateTimeLabelsFormat(dateFormat) {
    const config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.master.update(config);
  }

  updateYAxisFormatter() {
    const workMinutesPerDay = getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays);
    this.master.update({
      yAxis: {
        labels: {
          formatter: function() {
            return Math.ceil(this.value / workMinutesPerDay);
          }
        }
      }
    });
  }

  updateDetailsScatterplot() {
    const data = this.props.details;

    if (!data.series) {
      return;
    }

    const series = _.map(data.series, (s, k) => {
      return {
        ...s,
        id: k,
        data: _.filter(s.data, b => b.x >= this.props.effRange[0] && b.x <= this.props.effRange[1])
      };
    });

    let min = 100;
    let max = 0;
    _.forEach(series, s => {
      _.forEach(s.data, d => {
        if (min > d.x) min = d.x;
        if (max < d.x) max = d.x;
      });
    });

    this.chart.update(
      {
        xAxis: {
          min,
          max
        },
        series
      },
      true,
      true
    );
  }

  updateDetailsColumns() {
    const data = _.filter(this.props.details, b => {
      return b.x >= this.props.effRange[0] && b.x <= this.props.effRange[1];
    });

    if (!data.length) {
      return;
    }

    let min = 0;
    let max = 100;

    if (data && !!data.length) {
      min = _.first(data).x;
      max = _.last(data).x;
      if (!min) {
        min = 0;
      }
      if (!max) {
        max = 100;
      }
    }

    this.chart.update(
      {
        xAxis: { min, max },
        series: [
          {
            data
          }
        ]
      },
      true,
      true
    );
  }

  updateDetails() {
    if (this.props.chartType === 'scatterplot') {
      this.updateDetailsScatterplot();
    } else {
      this.updateDetailsColumns();
    }
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('fe');
    this.chart.update(newConfig.chart);
    this.master.update(newConfig.master);
  }

  onSelection(e) {
    var extremesObject = e.xAxis[0],
      min = extremesObject.min,
      max = extremesObject.max,
      xAxis = e.target.xAxis[0];

    xAxis.removePlotBand('mask-before');
    xAxis.addPlotBand({
      id: 'mask-before',
      from: xAxis.dataMin,
      to: min,
      color: chartUtils.ZOOM_MASK_COLOR,
      borderColor: chartUtils.ZOOM_MASK_BORDER,
      borderWidth: 1
    });

    xAxis.removePlotBand('mask-after');
    xAxis.addPlotBand({
      id: 'mask-after',
      from: max,
      to: xAxis.dataMax,
      color: chartUtils.ZOOM_MASK_COLOR,
      borderColor: chartUtils.ZOOM_MASK_BORDER,
      borderWidth: 1
    });

    if (this.master.resetBtn) {
      this.master.resetBtn.destroy();
    }
    this.master.resetBtn = this.master.renderer
      .button(
        'Reset zoom',
        null,
        null,
        () => {
          this.resetExtremes();
        },
        chartUtils.RESET_ZOOM_THEME,
        chartUtils.RESET_ZOOM_HOVER
      )
      .attr({
        align: 'right',
        title: 'Reset zoom level 1:1'
      })
      .addClass('highcharts-reset-zoom')
      .add(this.master.zoomGroupButton)
      .align(
        {
          align: 'right',
          x: -8,
          y: 8
        },
        false,
        null
      );

    const from = moment(min),
      to = moment(max);
    this.props.getDetailsData({
      startDate: from,
      endDate: to
    });

    return false;
  }

  resetExtremes() {
    this.master.xAxis[0].removePlotBand('mask-before');
    this.master.xAxis[0].removePlotBand('mask-after');
    this.props.getDetailsData(this.props.dates);
    this.master.resetBtn.destroy();
    delete this.master.resetBtn;
  }

  findPointsAtPosition(chart, targetX, targetY) {
    const points = [];
    for (let s = 0; s < chart.series.length; s++) {
      for (let i = 0; i < chart.series[s].data.length; i++) {
        const point = chart.series[s].data[i];
        if (point.x === targetX && point.y === targetY && point.card) {
          points.push({
            card: point.card,
            details: point.details,
            color: point.color
          });
        }
      }
    }
    return points;
  }

  onPointClick(comp, ev) {
    const isScatterPlot = !ev.cards;
    
    if (isScatterPlot) {
      // For scatterplot, find all points at the same position
      const points = comp.findPointsAtPosition(comp.chart, ev.x, ev.y);
      chartUtils.selectOnly(comp.chart, ev);
      comp.selectionHalo = chartUtils.drawSelectionHalo(comp.chart, comp.selectionHalo);
      
      comp.props.onPointClick({
        efficiency: ev.x,
        cycleTime: ev.y,
        cards: points.map(p => ({ details: p.details, card: p.card }))
      });
    } else {
      // For histogram, use existing logic
      comp.props.onPointClick({
        efficiency: ev.x,
        cycleTime: undefined,
        cards: ev.cards
      });
    }
  }

  tooltipFormatter(ev) {
    let details = '';
    const isScatterPlot = !ev.points;
    
    if (isScatterPlot) {
      // For scatterplot, find all points at the same position
      const points = this.findPointsAtPosition(this.chart, ev.point.x, ev.point.y);
      
      for (let i = 0; i < points.length; i++) {
        const point = points[i];
        const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
        details += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
      }
    } else {
      // For histogram, use existing logic
      const points = ev.points;
      for (let p = 0; p < points.length; p++) {
        if (points[p]) {
          const cards = points[p].point.cards;
          for (let i = 0; i < cards.length; i++) {
            if (cards[i]) {
              const point = cards[i];
              const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
              details += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
            }
          }
        }
      }
    }

    let tooltip;
    if (isScatterPlot) {
      const duration = `${ev.y} day${ev.y != 1 ? 's' : ''}`;
      tooltip = `
        <p class="tooltip-label-size" style="margin: 8px 8px 0;">Flow efficiency: ${ev.x}%</p>
        <p class="tooltip-label-size" style="margin: 4px 8px 0;">Cycle time: ${duration}</p> 
      `;
    } else if (this.props.wallmode) {
      tooltip = chartUtils.tvTooltipLead(`Frequency: ${ev.y}`, `Flow efficiency: ${ev.x}%`);
    } else {
      tooltip = `
        <p class="tooltip-label-size" style="margin: 8px 8px 0;">Flow efficiency: ${ev.x}%</p> 
        <p class="tooltip-label-size" style="margin: 4px 8px 0;">Frequency: ${ev.y}</p>
      `;
    }

    tooltip += details;

    tooltip += `<p class="tooltip-info-txt">Click on the ${isScatterPlot ? 'dot' : 'bar'} to see the details</p>`;

    return tooltip;
  }

  render() {
    return (
      <div id={this.props.id} className="chart_content chart_tooltip_style">
        <div
          ref={this.chartContainer}
          className={'chart_details ' + (!this.props.showNavigator ? 'chart_details-full ' : ' ')}
        ></div>
        <div
          style={{ display: this.props.showNavigator ? 'block' : 'none' }}
          ref={this.masterContainer}
          className="chart_master"
        ></div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getDetailsData: dates => dispatch(getDetailsData(dates))
  };
};

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    queueLists: state.filters.fe.selectedQueueLists,
    chartType: state.filters.fe.chartType,
    showNavigator: state.filters.fe.showNavigator,
    effRange: state.filters.fe.effRange,
    details: state.fe.details,
    isLoading: state.fe.isLoading,
    wallmode: state.app.wallmode,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(FlowEfficiencyChart));
